import { useCallback, useEffect, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { AnimatePresence, m } from "framer-motion";
import { Building2, Check, Pencil, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getFiscalTypesForCountry } from "@/config/fiscal-types";
import { useDefaultCountry } from "@/lib/hooks/use-default-country";

/**
 * Billing profile (fiscal details used on invoices) — 1:1 port of legacy
 * `src/components/billing/billing-profile-form.tsx`. Lives at the top of the
 * Invoices tab on the unified billing page. Talks to
 * GET/POST/PUT /api/account/billing/profile.
 */

interface BillingProfileData {
  id: string;
  profileName: string;
  businessName: string | null;
  vatNumber: string | null;
  registrationNumber: string | null;
  fiscalType: string;
  country: string;
  address: string;
  isDefault: boolean;
  /** What VIES said of `vatNumber` when the server saved or last re-checked it. */
  vatStatus?: "valid" | "invalid" | "unverified" | null;
}

export function BillingProfileForm() {
  const { t } = useLingui();
  const { country: defaultCountry } = useDefaultCountry();
  const countryCode = (defaultCountry || "ro").toLowerCase();

  const [profiles, setProfiles] = useState<BillingProfileData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [profileName, setProfileName] = useState("");
  const [fiscalType, setFiscalType] = useState("individual");
  const [businessName, setBusinessName] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [country, setCountry] = useState(countryCode.toUpperCase());
  const [address, setAddress] = useState("");

  const fiscalTypes = getFiscalTypesForCountry(country.toLowerCase());
  const selectedFiscal = fiscalTypes.find((ft) => ft.code === fiscalType);

  const fetchProfiles = useCallback(async () => {
    try {
      const data = await apiFetch.get<{ profiles?: BillingProfileData[] }>(
        "/api/account/billing/profile",
      );
      setProfiles(data.profiles || []);
    } catch {
      /* keep empty list */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProfiles();
  }, [fetchProfiles]);

  const resetForm = () => {
    setProfileName("");
    setFiscalType("individual");
    setBusinessName("");
    setVatNumber("");
    setRegistrationNumber("");
    setCountry(countryCode.toUpperCase());
    setAddress("");
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (p: BillingProfileData) => {
    setEditingId(p.id);
    setProfileName(p.profileName);
    setFiscalType(p.fiscalType);
    setBusinessName(p.businessName || "");
    setVatNumber(p.vatNumber || "");
    setRegistrationNumber(p.registrationNumber || "");
    setCountry(p.country);
    setAddress(p.address);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!profileName || !fiscalType || !country || !address) return;
    setSaving(true);

    try {
      const body = {
        ...(editingId && { id: editingId }),
        profileName,
        fiscalType,
        businessName: selectedFiscal?.requiresBusinessName ? businessName : null,
        vatNumber: vatNumber || null,
        registrationNumber: selectedFiscal?.requiresRegistrationNumber ? registrationNumber : null,
        country,
        address,
        // Only a new first profile becomes the default. Sent on an edit, this
        // un-defaulted the account's only profile — its invoices then fell
        // back to the payout data.
        ...(!editingId && { isDefault: profiles.length === 0 }),
      };

      if (editingId) {
        await apiFetch.put("/api/account/billing/profile", body);
      } else {
        await apiFetch.post("/api/account/billing/profile", body);
      }
      resetForm();
      void fetchProfiles();
    } catch {
      /* keep form open so user can retry */
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl border p-6">
        <div className="bg-muted h-6 w-48 animate-pulse rounded" />
        <div className="bg-muted mt-4 h-20 animate-pulse rounded-lg" />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="text-primary h-5 w-5" />
          <h2 className="text-foreground text-lg font-semibold">
            <Trans>Billing details</Trans>
          </h2>
        </div>
        {!showForm && (
          <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>
            <Plus className="mr-1 h-3 w-3" />
            <Trans>Add profile</Trans>
          </Button>
        )}
      </div>
      <p className="text-muted-foreground mt-1 text-sm">
        <Trans>Your fiscal information used on invoices</Trans>
      </p>

      {/* Existing profiles */}
      {profiles.length > 0 && (
        <div className="mt-4 space-y-2">
          {profiles.map((p) => (
            <div
              key={p.id}
              className={cn(
                "bg-background flex items-center justify-between rounded-lg border p-4",
                p.isDefault && "border-primary/30",
              )}
            >
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-foreground text-sm font-medium">{p.profileName}</p>
                  {p.isDefault && (
                    <span className="bg-primary/10 text-primary rounded px-1.5 py-0.5 text-[10px] font-medium">
                      Default
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground text-xs">
                  {p.businessName || p.profileName} · {p.fiscalType} · {p.country}
                </p>
                {p.vatNumber && (
                  <p className="text-muted-foreground text-xs">
                    VAT: {p.vatNumber}
                    {p.vatStatus === "valid" && <span className="text-success"> · VIES ✓</span>}
                    {p.vatStatus === "invalid" && (
                      <span className="text-destructive">
                        {" "}
                        · <Trans>Not found in VIES — invoiced with VAT</Trans>
                      </span>
                    )}
                    {p.vatStatus === "unverified" && (
                      <span>
                        {" "}
                        · <Trans>VIES did not answer — checked again at checkout</Trans>
                      </span>
                    )}
                  </p>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => startEdit(p)}
                aria-label={t`Edit billing profile`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 overflow-hidden"
          >
            <div className="space-y-4 rounded-lg border p-4">
              <div>
                <label className="text-foreground mb-1 block text-sm font-medium">
                  <Trans>Profile name</Trans>
                </label>
                <Input
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  placeholder={t`e.g. My Company Ltd, Personal`}
                />
              </div>

              {/* Fiscal type selector */}
              <div>
                <label className="text-foreground mb-1 block text-sm font-medium">
                  <Trans>Fiscal type</Trans>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {fiscalTypes.map((ft) => (
                    <button
                      key={ft.code}
                      type="button"
                      onClick={() => setFiscalType(ft.code)}
                      className={cn(
                        "rounded-lg border p-3 text-left text-sm transition-all",
                        fiscalType === ft.code
                          ? "border-primary bg-primary/5"
                          : "border-border bg-background hover:border-primary/30",
                      )}
                    >
                      <p className="text-foreground font-medium">{ft.localLabel}</p>
                      <p className="text-muted-foreground text-xs capitalize">
                        {ft.code.replace("_", " ")}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Conditional fields */}
              {selectedFiscal?.requiresBusinessName && (
                <div>
                  <label className="text-foreground mb-1 block text-sm font-medium">
                    <Trans>Company name</Trans>
                  </label>
                  <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
                </div>
              )}

              {(selectedFiscal?.requiresVatNumber || fiscalType !== "individual") && (
                <div>
                  <label className="text-foreground mb-1 block text-sm font-medium">
                    <Trans>VAT number</Trans>{" "}
                    {!selectedFiscal?.requiresVatNumber && (
                      <span className="text-muted-foreground text-xs">
                        (<Trans>optional</Trans>)
                      </span>
                    )}
                  </label>
                  <Input
                    value={vatNumber}
                    onChange={(e) => setVatNumber(e.target.value)}
                    placeholder="RO12345678"
                  />
                </div>
              )}

              {selectedFiscal?.requiresRegistrationNumber && (
                <div>
                  <label className="text-foreground mb-1 block text-sm font-medium">
                    <Trans>Registration number</Trans>
                  </label>
                  <Input
                    value={registrationNumber}
                    onChange={(e) => setRegistrationNumber(e.target.value)}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-foreground mb-1 block text-sm font-medium">
                    <Trans>Country</Trans>
                  </label>
                  <Input
                    value={country}
                    onChange={(e) => setCountry(e.target.value.toUpperCase())}
                    maxLength={2}
                    placeholder="RO"
                  />
                </div>
                <div>
                  <label className="text-foreground mb-1 block text-sm font-medium">
                    <Trans>Address</Trans>
                  </label>
                  <Input value={address} onChange={(e) => setAddress(e.target.value)} />
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSave} disabled={saving || !profileName || !address}>
                  <Check className="mr-1 h-3 w-3" />
                  {saving ? (
                    "…"
                  ) : editingId ? (
                    <Trans>Save changes</Trans>
                  ) : (
                    <Trans>Add profile</Trans>
                  )}
                </Button>
                <Button variant="ghost" onClick={resetForm}>
                  <Trans>Cancel</Trans>
                </Button>
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}
